import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Footprints, Thermometer, AlertTriangle, Activity,
  Users, Layers, Flame, Hand, Timer, ScanLine, CircleDot,
} from "lucide-react";
import { ASSETS } from "@/data/content";
import CroquisRecinto, { ZONA_PCT, enPorcentaje } from "@/components/CroquisRecinto";

// ─────────────────────────────────────────────────────────────────────────────
// Centro de mando — cuatro vistas que el operador enciende con un boton.
//
// Fuente de verdad: la maqueta aprobada (landing/DISENO-DASHBOARD-DEMO.md §6).
//
// ⚠️ LAS INSIGNIAS DE ESTADO Y LA LEYENDA "CÓMO LEER ESTA PANTALLA" SE QUITARON
// EL 6-AGO A PETICIÓN DEL CLIENTE. No fue un descuido: Neto pidió dejar fuera de
// la muestra conceptual los indicativos internos del proyecto (Fase 2, requiere
// equipo del cliente, funcionando hoy). No se reponen sin decisión suya.
//
// Lo que NO cambió, y es lo que hay que cuidar al editar este archivo: el cliente
// final es gobierno y presentarle una maqueta como producto terminado sigue siendo
// el riesgo mas caro del proyecto (alcance_v2.md §7). Sin las insignias, lo unico
// que sostiene la distincion son la banda inferior de <Escena> ("ejemplo de como
// se veria") y la redaccion de los textos. De ahi que sigan vigentes las dos
// prohibiciones de siempre:
//
//   1. La vista de marcha es la unica construida de verdad, asi que NO admite
//      datos de ejemplo disfrazados de reales. Sus numeros salen de archivos
//      del repo (ver NUMEROS_MARCHA) y no incluye una coincidencia positiva:
//      el motor nunca ha demostrado una (todas las corridas de storage/jobs.json
//      son matched:false).
//   2. Las otras tres vistas si muestran ejemplos, pero SIN porcentajes de
//      confianza inventados. Lenguaje cualitativo, que ademas es el que entienden
//      los no tecnicos que van a estar en la sala.
// ─────────────────────────────────────────────────────────────────────────────

const VISTAS = [
  { id: "marcha", icono: Footprints, nombre: "Identificación por forma de caminar" },
  { id: "termica", icono: Thermometer, nombre: "Visión térmica e infrarroja" },
  { id: "contingencias", icono: AlertTriangle, nombre: "Avisos de contingencias" },
  { id: "flujos", icono: Activity, nombre: "Flujos de gente y aglomeraciones" },
];

// Numeros de la vista de marcha. Cada uno tiene un archivo detras; si alguien
// cambia esta tabla, tiene que poder citar la fuente.
const NUMEROS_MARCHA = [
  { k: "Punto de corte de decisión", v: "3.0", tono: "" },
  { k: "Entre personas distintas", v: "3.93 – 4.65", tono: "good" },
  { k: "Medición de extremo a extremo", v: "3.962 · no coincide", tono: "good" },
];

// ─────────────────────────────────────────────────────────────────────────────
// Los cuatro eventos que pidio Neto: como EMPIEZA a detectarse cada cosa.
// Tres pasos por evento, y el presentador los avanza a mano para poder explicar
// cada uno. Se declaran como datos y los pinta <Escena>; asi los 12 estados no
// cuestan 12 veces el trabajo ni se desincronizan entre si.
//
// Cada paso lleva un bloque `croquis` que <CroquisRecinto> convierte en el plano
// del recinto con la gente encima. Las manchas difuminadas y las flechas que
// habia antes se quitaron: sobre un plano con gente ya no explican nada, y el
// llenado de cada zona ahora sale de `filas[].barra` — el MISMO numero que la
// tabla de la derecha, para que el dibujo no pueda contradecir a los datos.
// Los recuadros del paso 3 se posicionan con ZONA_PCT, asi caen exactamente
// sobre la zona del plano en vez de con numeros a mano.
// ─────────────────────────────────────────────────────────────────────────────

// El recinto con la gente en su sitio. Es el fondo de los avisos que NO son de
// aforo (incendio, riña): ahi las zonas se quedan igual en los tres pasos, para
// que se vea que lo que cambia es el aviso y no la cantidad de gente.
const GENTE_TRANQUILA = {
  zonas: { "Acceso norte": 0.5, "Plaza central": 0.55, "Salida sur": 0.3 },
  tonos: { "Acceso norte": "good", "Plaza central": "good", "Salida sur": "good" },
};

// Focos de calor que el sistema ya conoce y que NO disparan aviso. Estan en el
// plano para que el paso 2 del incendio signifique algo: lo que avisa es un foco
// nuevo, no cualquier cosa caliente del recinto. Sin esto, "detecta calor" se
// leeria como que el sistema avisaria por las cocinas.
// Los tres puntos donde el recinto se angosta: la puerta que sale del acceso
// norte, el corredor de la plaza a la salida, y la boca de la salida sur. Un
// cuello de botella se forma en el PASO entre dos zonas, no dentro de una zona,
// y por eso se marcan como puntos.
const PUNTOS_PASO = [
  { id: "puerta-norte", x: 50, y: 33, ang: 24 },
  { id: "corredor-sur", x: 110, y: 56, ang: 24 },
  // Pegado al borde de abajo: su etiqueta va arriba porque no cabe debajo.
  { id: "boca-salida", x: 140, y: 72, ang: 0, arriba: true },
];

// Los que no se nombran quedan fluyendo. Así cada paso declara solo lo que cambia.
const puntosPaso = (estados) =>
  PUNTOS_PASO.map((p) => ({ ...p, estado: estados[p.id] || "fluye" }));

// Ojo con las coordenadas: la etiqueta se dibuja debajo del foco, y la esquina
// inferior izquierda del plano la ocupa la escala de calor (y 70-79).
const FOCOS_CONOCIDOS = [
  { x: 24, y: 56, r: 2.6, etiqueta: "COCINAS" },
  { x: 152, y: 44, r: 2.2, etiqueta: "GENERADOR" },
];

const EVENTOS = {
  flujos: [
    {
      id: "aglomeracion",
      icono: Users,
      nombre: "Aglomeración",
      mide: "Cuánta gente hay en cada zona del evento",
      pasos: [
        {
          titulo: "Vigilancia activa",
          emoji: "🎦",
          dice: "El sistema cuenta cuánta gente hay en cada zona. Todas están dentro de su capacidad.",
          croquis: { velocidad: 1 },
          overlays: [],
          filas: [
            { k: "Acceso norte", v: "142 / 200", tono: "good", barra: 71 },
            { k: "Plaza central", v: "210 / 350", tono: "good", barra: 60 },
            { k: "Salida sur", v: "89 / 250", tono: "good", barra: 36 },
          ],
        },
        {
          titulo: "Advertencia",
          emoji: "⚠️",
          dice: "La plaza central se llena y no se vacía: entra gente y casi nadie sale. La zona se pone ámbar, y es lo que el operador ve primero.",
          // La gente afloja el paso y la plaza se llena hasta el 91%.
          croquis: { velocidad: 0.5 },
          overlays: [],
          filas: [
            { k: "Acceso norte", v: "145 / 200", tono: "good", barra: 72 },
            { k: "Plaza central", v: "318 / 350", tono: "warn", barra: 91 },
            { k: "Salida sur", v: "94 / 250", tono: "good", barra: 38 },
          ],
        },
        {
          titulo: "Incidente crítico",
          emoji: "🚨",
          dice: "Se rebasa el punto de corte y entra el aviso al centro de mando. El sistema avisa; la persona decide qué hacer.",
          croquis: { velocidad: 0.18 },
          overlays: [
            { tipo: "box", ...ZONA_PCT["Plaza central"], tag: "Aglomeración · aviso enviado", alerta: true },
          ],
          filas: [
            { k: "Acceso norte", v: "147 / 200", tono: "good", barra: 74 },
            { k: "Plaza central", v: "341 / 350", tono: "bad", barra: 97 },
            { k: "Salida sur", v: "96 / 250", tono: "good", barra: 38 },
          ],
          aviso: { hora: "14:28", texto: "Aglomeración · Plaza central" },
        },
      ],
    },
    {
      id: "cuello",
      icono: Layers,
      nombre: "Cuello de botella",
      mide: "Hacia dónde se mueve la gente, a qué velocidad, y cómo van los puntos donde el recinto se angosta",
      pasos: [
        {
          titulo: "Vigilancia activa",
          emoji: "🎦",
          dice: "El sistema vigila los tres puntos donde el recinto se angosta: la puerta del acceso norte, el corredor a la salida y la boca de la salida sur. Los tres fluyen.",
          croquis: {
            velocidad: 1,
            zonas: { "Acceso norte": 0.55, "Plaza central": 0.5 },
            tonos: { "Acceso norte": "good", "Plaza central": "good" },
            cuellos: puntosPaso({}),
          },
          overlays: [],
          filas: [
            { k: "Salida sur", v: "89 / 250", tono: "good", barra: 36 },
            { k: "Puntos de paso", v: "los 3 fluyendo", tono: "good" },
            { k: "Velocidad de paso", v: "normal", tono: "good" },
            { k: "Direcciones", v: "repartidas", tono: "good" },
          ],
        },
        {
          titulo: "Advertencia",
          emoji: "⚠️",
          // Aqui esta el valor de la vista: el sistema señala donde TODAVIA NO
          // hay cuello de botella pero lo va a haber, y lo deduce de hacia donde
          // va la gente — de ahi las flechas de flujo sobre la ruta.
          dice: "Todos empiezan a tomar la misma ruta hacia la salida. Ahí todavía no hay cuello de botella, pero el sistema ya marca la boca de la salida como el punto donde se va a formar.",
          croquis: {
            velocidad: 0.45,
            converge: true,
            zonas: { "Acceso norte": 0.5, "Plaza central": 0.58 },
            tonos: { "Acceso norte": "good", "Plaza central": "good" },
            cuellos: puntosPaso({ "boca-salida": "riesgo" }),
          },
          overlays: [],
          filas: [
            { k: "Salida sur", v: "184 / 250", tono: "warn", barra: 74 },
            { k: "Boca de la salida", v: "posible cuello", tono: "warn" },
            { k: "Velocidad de paso", v: "bajando", tono: "warn" },
            { k: "Direcciones", v: "convergiendo", tono: "warn" },
          ],
        },
        {
          titulo: "Incidente crítico",
          emoji: "🚨",
          dice: "El cuello de botella se formó en la boca de la salida y el sistema avisa. Y marca el corredor de atrás como el siguiente en riesgo: la fila se propaga hacia atrás, así que ahí es donde hay que abrir paso.",
          croquis: {
            velocidad: 0.06,
            converge: true,
            zonas: { "Acceso norte": 0.48, "Plaza central": 0.62 },
            tonos: { "Acceso norte": "good", "Plaza central": "warn" },
            cuellos: puntosPaso({ "boca-salida": "cuello", "corredor-sur": "riesgo" }),
          },
          // Sin recuadro sobre la zona: lo que marca el aviso es el punto de
          // paso, y envolver la salida sur completa contradecia eso.
          overlays: [],
          filas: [
            { k: "Salida sur", v: "241 / 250", tono: "bad", barra: 96 },
            { k: "Boca de la salida", v: "cuello de botella", tono: "bad" },
            { k: "Corredor a la salida", v: "posible cuello", tono: "warn" },
            { k: "Velocidad de paso", v: "detenida", tono: "bad" },
          ],
          aviso: { hora: "14:35", texto: "Cuello de botella · Boca de la salida sur" },
        },
      ],
    },
  ],
  contingencias: [
    {
      id: "incendio",
      icono: Flame,
      nombre: "Incendio",
      mide: "El calor de cada zona: su color, su forma y si está creciendo",
      pasos: [
        {
          titulo: "Vigilancia activa",
          emoji: "🎦",
          // El plano se lee en clave TÉRMICA, no por aforo: en un incendio la
          // cantidad de gente no es lo que el sistema vigila, y pintar las zonas
          // por aforo dejaba esta vista igualita a la de flujos.
          dice: "El sistema vigila el calor del recinto. Los focos que ya conoce —las cocinas, el generador— llevan toda la noche encendidos y no lo hacen avisar.",
          croquis: { velocidad: 1, modo: "termico", ...GENTE_TRANQUILA, focosConocidos: FOCOS_CONOCIDOS },
          overlays: [],
          filas: [
            { k: "Zona oriente", v: "sin novedad", tono: "good" },
            { k: "Zona poniente", v: "sin novedad", tono: "good" },
            { k: "Focos de calor", v: "2 conocidos", tono: "good" },
          ],
        },
        {
          titulo: "Advertencia",
          emoji: "⚠️",
          dice: "Aparece un foco que no estaba, crece y tiene el color del fuego. Todavía no avisa: primero confirma que no se apaga.",
          croquis: {
            velocidad: 0.9,
            modo: "termico",
            ...GENTE_TRANQUILA,
            focosConocidos: FOCOS_CONOCIDOS,
            mitades: { oriente: "warn" },
            foco: { x: 130, y: 41, r: 3.5 },
          },
          overlays: [
            { tipo: "box", ...enPorcentaje({ x: 123, y: 34, w: 14, h: 14 }), tag: "Foco nuevo · en observación", alerta: false },
          ],
          filas: [
            { k: "Zona oriente", v: "foco nuevo creciendo", tono: "warn" },
            { k: "Zona poniente", v: "sin novedad", tono: "good" },
            { k: "Focos de calor", v: "1 nuevo en observación", tono: "warn" },
          ],
        },
        {
          titulo: "Incidente crítico",
          emoji: "🚨",
          dice: "El foco sigue creciendo y se rebasa el punto de corte. Entra el aviso; el operador confirma antes de actuar.",
          croquis: {
            velocidad: 0.7,
            modo: "termico",
            ...GENTE_TRANQUILA,
            // La gente se retira de la zona del foco por su cuenta. El sistema
            // avisa: no evacua, no cierra accesos, no decide nada.
            zonas: { "Acceso norte": 0.5, "Plaza central": 0.62, "Salida sur": 0.12 },
            focosConocidos: FOCOS_CONOCIDOS,
            mitades: { oriente: "bad" },
            foco: { x: 130, y: 41, r: 10 },
          },
          overlays: [
            {
              tipo: "box",
              ...enPorcentaje({ x: 117, y: 28, w: 26, h: 26 }),
              tag: "Posible incendio · aviso enviado",
              alerta: true,
            },
          ],
          filas: [
            { k: "Zona oriente", v: "posible incendio", tono: "bad" },
            { k: "Zona poniente", v: "sin novedad", tono: "good" },
            { k: "Focos de calor", v: "1 nuevo confirmado", tono: "bad" },
          ],
          aviso: { hora: "14:31", texto: "Posible incendio · Zona oriente" },
        },
      ],
    },
    {
      id: "rina",
      icono: Hand,
      nombre: "Riña",
      mide: "Qué tan cerca están dos personas y qué tan bruscos son sus movimientos",
      pasos: [
        {
          titulo: "Vigilancia activa",
          emoji: "🎦",
          dice: "Dos personas caminan separadas. El sistema mide su distancia y la suavidad de su movimiento.",
          // Los dos puntos marcados van dentro de la plaza central, que es la
          // zona que nombra el aviso del paso 3.
          croquis: {
            velocidad: 1,
            modo: "alerta",
            ...GENTE_TRANQUILA,
            destacados: [{ x: 66, y: 52 }, { x: 98, y: 40 }],
          },
          overlays: [
            { tipo: "box", ...enPorcentaje({ x: 61, y: 47, w: 10, h: 10 }), tag: "Persona 001", alerta: false },
            { tipo: "box", ...enPorcentaje({ x: 93, y: 35, w: 10, h: 10 }), tag: "Persona 002", alerta: false },
          ],
          filas: [
            { k: "Distancia entre ambas", v: "amplia", tono: "good" },
            { k: "Movimiento", v: "suave", tono: "good" },
            { k: "Otras personas cerca", v: "ninguna", tono: "good" },
          ],
        },
        {
          titulo: "Advertencia",
          emoji: "⚠️",
          dice: "Se acercan de frente y su movimiento se vuelve brusco. Es la combinación que el sistema busca.",
          croquis: {
            velocidad: 0.9,
            modo: "alerta",
            ...GENTE_TRANQUILA,
            destacados: [{ x: 80, y: 48 }, { x: 88, y: 49 }],
            cumulo: { x: 84, y: 48, r: 8, n: 2 },
          },
          overlays: [
            { tipo: "box", ...enPorcentaje({ x: 75, y: 43, w: 10, h: 10 }), tag: "Persona 001", alerta: false },
            { tipo: "box", ...enPorcentaje({ x: 83, y: 44, w: 10, h: 10 }), tag: "Persona 002", alerta: false },
          ],
          filas: [
            { k: "Distancia entre ambas", v: "acercándose", tono: "warn" },
            { k: "Movimiento", v: "brusco", tono: "warn" },
            { k: "Otras personas cerca", v: "2 se detienen", tono: "warn" },
          ],
        },
        {
          titulo: "Incidente crítico",
          emoji: "🚨",
          dice: "El sistema marca a las dos juntas y avisa. No juzga qué pasó: eso lo resuelve quien está viendo la pantalla.",
          croquis: {
            velocidad: 0.8,
            modo: "alerta",
            ...GENTE_TRANQUILA,
            destacados: [{ x: 83, y: 48 }, { x: 87, y: 48 }],
            cumulo: { x: 85, y: 48, r: 9, n: 4 },
          },
          overlays: [
            {
              tipo: "box",
              ...enPorcentaje({ x: 76, y: 40, w: 20, h: 18 }),
              tag: "Posible riña · aviso enviado",
              alerta: true,
            },
          ],
          filas: [
            { k: "Distancia entre ambas", v: "contacto", tono: "bad" },
            { k: "Movimiento", v: "muy brusco", tono: "bad" },
            { k: "Otras personas cerca", v: "4 se congregan", tono: "warn" },
          ],
          aviso: { hora: "14:12", texto: "Posible riña · Plaza central" },
        },
      ],
    },
  ],
};

// ─── Piezas ──────────────────────────────────────────────────────────────────

const TONOS = { good: "text-emerald-400", warn: "text-amber-300", bad: "text-red-400", "": "text-slate-400" };
const BARRAS = { good: "bg-emerald-500", warn: "bg-amber-400", bad: "bg-red-500", "": "bg-blue-500" };

function Fila({ f }) {
  return (
    <div className="py-2 border-b border-slate-800/60 last:border-0">
      <div className="flex items-center justify-between gap-3 font-mono text-[11.5px]">
        <span className="text-slate-200">{f.k}</span>
        <span className={`tabular-nums ${TONOS[f.tono || ""]}`}>{f.v}</span>
      </div>
      {typeof f.barra === "number" && (
        <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden mt-1.5">
          <div className={`h-full rounded-full ${BARRAS[f.tono || ""]}`} style={{ width: `${f.barra}%` }} />
        </div>
      )}
    </div>
  );
}

function Tarjeta({ titulo, extra, children, testid }) {
  return (
    <div data-testid={testid} className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden">
      <div className="px-3.5 py-2.5 border-b border-slate-800 flex items-center justify-between gap-3">
        <span className="font-mono text-[10.5px] uppercase tracking-widest text-slate-400">{titulo}</span>
        {extra && <span className="font-mono text-[10px] text-slate-500">{extra}</span>}
      </div>
      <div className="px-3.5 py-2">{children}</div>
    </div>
  );
}

function Nota({ children, tono = "blue" }) {
  const borde = tono === "warn" ? "border-l-amber-500 bg-amber-500/[0.07]" : "border-l-blue-500 bg-blue-500/[0.07]";
  return (
    <p className={`rounded-lg border border-slate-800 border-l-[3px] ${borde} px-4 py-3 text-[13px] leading-relaxed text-slate-300`}>
      {children}
    </p>
  );
}

// Recuadros, manchas y flechas encima de la escena. Van en % del contenedor,
// asi que sirven igual sobre un degradado, sobre video o sobre el croquis.
function Overlays({ overlays }) {
  return overlays.map((o, i) => {
    if (o.tipo === "heat") {
      return (
        <div
          key={i}
          className="absolute rounded-full"
          style={{
            left: `${o.x}%`, top: `${o.y}%`, width: `${o.w}%`, height: `${o.h}%`,
            background: o.color, filter: "blur(14px)",
          }}
        />
      );
    }
    if (o.tipo === "arrow") {
      return (
        <div
          key={i}
          className="absolute h-0.5 rounded"
          style={{
            left: `${o.x}%`, top: `${o.y}%`, width: `${o.w}%`,
            background: "linear-gradient(90deg,transparent,#38bdf8)",
          }}
        />
      );
    }
    return (
      <div
        key={i}
        className={`absolute border-2 rounded-sm ${o.alerta ? "border-amber-300" : "border-blue-400"}`}
        style={{ left: `${o.x}%`, top: `${o.y}%`, width: `${o.w}%`, height: `${o.h}%` }}
      >
        <span
          className={`absolute -top-[19px] left-0 whitespace-nowrap font-mono text-[9px] px-1.5 py-0.5 rounded-sm ${
            o.alerta ? "bg-amber-300 text-slate-950" : "bg-blue-500 text-white"
          }`}
        >
          {o.tag}
        </span>
      </div>
    );
  });
}

// El marco de la escena: barra arriba, barra abajo y los overlays encima de lo
// que sea que va dentro — el croquis del recinto o el video real del motor.
//
// Antes esta funcion tambien pintaba escenas de degradado (`ESCENAS`) para los
// eventos de contingencias. Se fueron con el croquis: una mancha de color sobre
// un rectangulo vacio no explicaba nada, y dejarlas seria codigo muerto que la
// proxima sesion tendria que descifrar.
function Escena({ overlays = [], arriba, abajo, children }) {
  return (
    <div className="relative rounded-xl overflow-hidden border border-slate-800 bg-black min-h-[200px] sm:min-h-[288px]">
      {/* Las dos bandas envuelven (flex-wrap) a proposito: con los nombres de
          paso del 6-ago ("Vigilancia activa", "Incidente critico") el texto ya no
          cabe en una linea en celular, y sin wrap se encimaba con el titulo. */}
      {arriba && (
        <div className="absolute top-0 inset-x-0 z-20 flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 px-3 py-2 bg-gradient-to-b from-slate-950/90 to-transparent font-mono text-[9px] sm:text-[10px] leading-tight text-slate-300">
          {arriba}
        </div>
      )}

      <div className="relative">
        {children}
        <Overlays overlays={overlays} />
      </div>

      {abajo && (
        <div className="absolute bottom-0 inset-x-0 z-20 flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 px-3 py-2 bg-gradient-to-t from-slate-950/90 to-transparent font-mono text-[9px] sm:text-[10px] leading-tight text-slate-300">
          {abajo}
        </div>
      )}
    </div>
  );
}

// Un evento con sus tres pasos. El presentador los avanza a mano: asi lleva el
// ritmo y explica cada uno, en vez de que la animacion corra sola.
function VistaEventos({ eventos, vistaId }) {
  const [act, setAct] = useState(0);
  const [paso, setPaso] = useState(0);
  const ev = eventos[act];
  const p = ev.pasos[paso];

  const cambiarEvento = (i) => { setAct(i); setPaso(0); };

  return (
    <div className="flex flex-col gap-4">
      {/* selector de evento */}
      <div className="flex flex-wrap gap-2">
        {eventos.map((e, i) => (
          <button
            key={e.id}
            data-testid={`evento-${e.id}`}
            onClick={() => cambiarEvento(i)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs transition-colors ${
              i === act
                ? "border-blue-500 bg-blue-600/20 text-white"
                : "border-slate-800 text-slate-400 hover:text-white hover:border-slate-600"
            }`}
          >
            <e.icono className="h-3.5 w-3.5" /> {e.nombre}
          </button>
        ))}
      </div>

      <div className="grid xl:grid-cols-[1.55fr_1fr] gap-4 items-start">
        <Escena
          overlays={p.overlays}
          arriba={<>
            <span className="flex items-center gap-2 text-white">
              <CircleDot className="h-3.5 w-3.5 text-slate-400" /> {ev.nombre.toUpperCase()}
            </span>
            <span>
              {p.croquis.modo === "termico" ? "VIGILANCIA TÉRMICA" : "PLANO DEL RECINTO"} · PASO{" "}
              {paso + 1} DE 3 · {p.emoji} {p.titulo.toUpperCase()}
            </span>
          </>}
          abajo={<>
            <span className="hidden sm:inline">EL OPERADOR CONFIRMA ANTES DE ACTUAR</span>
            <span className="text-sky-300">CADA PUNTO ES UNA PERSONA · EJEMPLO DE CÓMO SE VERÍA</span>
          </>}
        >
          <CroquisRecinto filas={p.filas} croquis={p.croquis} />
        </Escena>

        <div className="flex flex-col gap-4 min-w-0">
          {/* Los tres pasos. En una columna en celular y en tres a partir de sm:
              con los nombres nuevos (6-ago) ya no caben tres en una fila angosta. */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid={`pasos-${ev.id}`}>
            {ev.pasos.map((s, i) => (
              <button
                key={s.titulo}
                data-testid={`paso-${ev.id}-${i + 1}`}
                onClick={() => setPaso(i)}
                className={`px-2 py-2 rounded-md border text-[11px] font-mono leading-tight transition-colors flex items-center justify-center gap-1.5 text-center ${
                  i === paso
                    ? "border-sky-500 bg-sky-500/15 text-sky-200"
                    : "border-slate-800 text-slate-500 hover:text-slate-200 hover:border-slate-600"
                }`}
              >
                <span aria-hidden="true">{s.emoji}</span>
                <span>{s.titulo}</span>
              </button>
            ))}
          </div>

          <Tarjeta titulo="Qué mide" testid={`mide-${vistaId}`}>
            <p className="py-2 text-[13px] leading-relaxed text-slate-300">{ev.mide}.</p>
          </Tarjeta>

          <Tarjeta titulo="Qué está pasando" extra={p.aviso ? p.aviso.hora : null} testid={`estado-${vistaId}`}>
            {p.filas.map((f) => <Fila key={f.k} f={f} />)}
          </Tarjeta>

          {p.aviso && (
            <Tarjeta titulo="Avisos" testid={`avisos-${vistaId}`}>
              <Fila f={{ k: p.aviso.texto, v: p.aviso.hora, tono: "bad" }} />
            </Tarjeta>
          )}

          <Nota tono="warn">{p.dice}</Nota>
        </div>
      </div>
    </div>
  );
}

function VistaMarcha() {
  return (
    <div className="grid xl:grid-cols-[1.55fr_1fr] gap-4 items-start">
      <Escena
        arriba={<>
          <span className="flex items-center gap-2 text-white">
            <CircleDot className="h-3.5 w-3.5 text-emerald-500" /> CÁM-01 · ACCESO NORTE
          </span>
          <span>RESULTADO PROCESADO CON ANTELACIÓN</span>
        </>}
        abajo={<>
          <span className="text-blue-300">SIN ROSTRO EN ESTA VISTA · SOLO CUERPO COMPLETO</span>
          <span className="text-slate-400">VIDEO REAL, ANOTADO POR EL MOTOR</span>
        </>}
      >
        {/* Los recuadros vienen quemados del motor: no se dibujan encima. */}
        <div className="relative scanline">
          <video
            data-testid="rgb-feed-video"
            src="/media/gait-feed.mp4"
            autoPlay
            muted
            loop
            playsInline
            className="w-full aspect-video object-cover"
          />
          <div className="absolute inset-0 tech-grid opacity-20 pointer-events-none" />
        </div>
      </Escena>

      <div className="flex flex-col gap-4 min-w-0">
        <div className="grid grid-cols-2 gap-3">
          <div data-testid="stat-personas" className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
            <Users className="h-5 w-5 text-blue-400" />
            <div className="font-display font-bold text-3xl text-white tabular-nums mt-3">1</div>
            <div className="text-[11px] text-slate-500">Persona en cuadro</div>
          </div>
          <div data-testid="stat-tiempo" className="rounded-xl border border-slate-800 bg-slate-900/60 p-4">
            <Timer className="h-5 w-5 text-slate-400" />
            <div className="font-display font-bold text-3xl text-white tabular-nums mt-3">12–27</div>
            <div className="text-[11px] text-slate-500">Minutos por análisis</div>
          </div>
        </div>

        <Tarjeta titulo="Cómo decide" testid="numeros-marcha">
          {NUMEROS_MARCHA.map((f) => <Fila key={f.k} f={f} />)}
        </Tarjeta>

        <Nota>
          <strong className="text-white">Lo importante es que sabe decir “no sé quién es”.</strong>{" "}
          Con personas distintas la distancia se va muy por encima del punto de corte, así que no
          las confunde. En un caso de gobierno señalar a la persona equivocada tiene un costo que
          no se repara, y por eso preferimos un sistema que se niegue a inventar.
        </Nota>
      </div>
    </div>
  );
}

function VistaTermica() {
  const filas = [
    { k: "De noche", v: "sigue viendo", tono: "good" },
    { k: "Con humo", v: "sigue viendo", tono: "good" },
    { k: "Zonas oscuras", v: "cubiertas", tono: "good" },
    { k: "Personas ocultas", v: "detecta calor", tono: "warn" },
  ];
  return (
    <div className="grid xl:grid-cols-[1.55fr_1fr] gap-4 items-start">
      <Escena
        arriba={<>
          <span className="flex items-center gap-2 text-white">
            <Thermometer className="h-3.5 w-3.5 text-orange-400" /> CÁM-02 · TÉRMICA
          </span>
          <span>VISIBILIDAD NOCTURNA</span>
        </>}
        abajo={<>
          <span className="text-orange-300">PALETA DE CALOR · SIMULADA SOBRE VIDEO RGB</span>
          <span className="text-slate-400">EJEMPLO DE CÓMO SE VERÍA</span>
        </>}
      >
        <div className="relative">
          <video
            data-testid="thermal-feed-video"
            src="/media/gait-feed.mp4"
            autoPlay
            muted
            loop
            playsInline
            className="w-full aspect-video object-cover"
            style={{ filter: "grayscale(1) contrast(1.4) brightness(0.8) sepia(1) hue-rotate(330deg) saturate(4)" }}
          />
          <div className="absolute inset-0 tech-grid opacity-15 pointer-events-none" />
        </div>
      </Escena>

      <div className="flex flex-col gap-4 min-w-0">
        <Tarjeta titulo="Para qué sirve" testid="termica-usos">
          {filas.map((f) => <Fila key={f.k} f={f} />)}
        </Tarjeta>
        <Nota>
          La vista térmica muestra el calor en lugar de la luz, así que funciona igual de noche y
          con humo. <strong className="text-white">Depende de que las cámaras o los drones traigan
          ese sensor</strong>, que es equipo del cliente, no del software.
        </Nota>
      </div>
    </div>
  );
}

// ─── Pagina ──────────────────────────────────────────────────────────────────

export default function DemoDashboard() {
  const [vista, setVista] = useState("marcha");
  const [reloj, setReloj] = useState("14:32:09");

  // Solo el reloj de pared. Los contadores que se movian solos (fps, latencia,
  // personas en cuadro) se quitaron: prometian tiempo real cuando un analisis
  // tarda entre 12 y 27 minutos.
  useEffect(() => {
    const id = setInterval(() => setReloj(new Date().toTimeString().slice(0, 8)), 1000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col">
      <header data-testid="demo-header" className="border-b border-slate-800 bg-slate-900/80 backdrop-blur">
        <div className="px-4 lg:px-6 h-14 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/" data-testid="demo-back-btn" className="flex items-center gap-1.5 text-slate-400 hover:text-white text-sm shrink-0">
              <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Volver</span>
            </Link>
            <div className="h-5 w-px bg-slate-700" />
            <img src={ASSETS.logo} alt="" className="h-6 w-6 object-contain" />
            <span className="font-display font-bold text-white truncate">Centro de Mando</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline font-mono text-[10px] text-slate-500 tracking-wider">
              ANÁLISIS PRE-PROCESADO
            </span>
            <span className="font-mono text-sm text-white tabular-nums">{reloj}</span>
          </div>
        </div>

        {/* Aqui iba la leyenda "Como leer esta pantalla" con las tres insignias.
            Neto pidio quitarla el 6-ago. Ver el encabezado de este archivo. */}
      </header>

      {/* Interruptores de vista. Reemplazan la barra lateral que tenia seis
          botones pintados que no hacian nada. */}
      <div data-testid="switcher" className="px-4 lg:px-6 py-4 border-b border-slate-800 grid gap-2.5 sm:grid-cols-2 xl:grid-cols-4">
        {VISTAS.map((v) => {
          const activo = v.id === vista;
          return (
            <button
              key={v.id}
              data-testid={`vista-${v.id}`}
              aria-pressed={activo}
              onClick={() => setVista(v.id)}
              className={`text-left rounded-lg border p-3 flex flex-col gap-2 transition-colors ${
                activo
                  ? "border-blue-500 bg-blue-600/15 text-white"
                  : "border-slate-800 bg-slate-900/40 text-slate-400 hover:text-white hover:border-slate-600"
              }`}
            >
              <span className="font-display font-semibold text-sm leading-tight flex items-start gap-2">
                <v.icono className="h-4 w-4 mt-0.5 shrink-0" /> {v.nombre}
              </span>
            </button>
          );
        })}
      </div>

      <main className="flex-1 min-w-0 p-4 lg:px-6 overflow-auto">
        {vista === "marcha" && <VistaMarcha />}
        {vista === "termica" && <VistaTermica />}
        {vista === "contingencias" && <VistaEventos eventos={EVENTOS.contingencias} vistaId="contingencias" />}
        {vista === "flujos" && <VistaEventos eventos={EVENTOS.flujos} vistaId="flujos" />}
      </main>
    </div>
  );
}
